import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useEffect, useState } from 'react';

import { cardsScoreCount } from '../utils';
import { CollectionBonusProgressBar } from './CollectionBonusProgressBar';
import type {
  AnimatedBonusProgressBar_anyCardInterface,
  AnimatedBonusProgressBar_cardCollection,
} from './__generated__/index.graphql';

type Props = {
  cardCollection: AnimatedBonusProgressBar_cardCollection;
  newCards: AnimatedBonusProgressBar_anyCardInterface[];
  animatingSlotIndex: Nullable<number>;
  onAnimationEnd: () => void;
  onThresholdReached: (isLastThreshold: boolean) => void;
};

const useCurrentScore = ({
  cardCollection,
  baseScore,
  cardScore,
  onThresholdReached,
  onAnimationEnd,
  shouldAnimate,
}: Pick<Props, 'cardCollection' | 'onThresholdReached' | 'onAnimationEnd'> & {
  baseScore: number;
  cardScore: number;
  shouldAnimate: boolean;
}) => {
  const [currentScoreIndex, setCurrentScoreIndex] = useState<number>(0);

  const targetScore = baseScore + cardScore;
  const thresholdsInBetween =
    cardCollection?.bonusThresholds
      ?.map(threshold => threshold.threshold)
      .filter(threshold => threshold > baseScore && threshold <= targetScore) ||
    [];

  const scoreSteps = [
    ...new Set([
      baseScore,
      ...[
        ...thresholdsInBetween.map(threshold => [
          threshold,
          // add this progress point to show 0% progress bar state (beginning of a threshold)
          threshold + 0.1,
        ]),
      ].flat(),
      targetScore,
    ]),
  ];

  useEffect(() => {
    // start the animation after progressbar is in baseScore position
    // this is to avoid the progressbar to jump to the baseScore position on first render
    if (shouldAnimate) {
      setCurrentScoreIndex(1);
    }
  }, [shouldAnimate]);

  const currentScore = scoreSteps[currentScoreIndex];

  return {
    currentScore,
    onAnimationEnd: async () => {
      if (currentScoreIndex < scoreSteps.length - 1) {
        const isThreshold = cardCollection.bonusThresholds.some(
          threshold => threshold.threshold === currentScore
        );
        if (isThreshold) {
          const isLastThreshold =
            currentScore >=
            (cardCollection.bonusThresholds.at(-1)?.threshold || Infinity);
          onThresholdReached(isLastThreshold);
        }
        setCurrentScoreIndex(currentScoreIndex + 1);
      } else {
        onAnimationEnd();
      }
    },
  };
};

const SingleCardBonusProgressBar = ({
  baseScore,
  cardScore,
  cardCollection,
  onThresholdReached,
  onAnimationEnd: onAnimationEndProp,
  shouldAnimate,
}: {
  baseScore: number;
  cardScore: number;
  cardCollection: AnimatedBonusProgressBar_cardCollection;
  onThresholdReached: (isLastThreshold: boolean) => void;
  onAnimationEnd: () => void;
  shouldAnimate: boolean;
}) => {
  const { currentScore, onAnimationEnd } = useCurrentScore({
    cardCollection,
    baseScore,
    cardScore,
    onThresholdReached,
    onAnimationEnd: onAnimationEndProp,
    shouldAnimate,
  });

  return (
    <CollectionBonusProgressBar
      cardCollection={cardCollection}
      score={currentScore}
      onAnimationEnd={onAnimationEnd}
    />
  );
};

export const AnimatedBonusProgressBar = ({
  cardCollection,
  newCards,
  animatingSlotIndex,
  onAnimationEnd,
  onThresholdReached,
}: Props) => {
  return (
    <SingleCardBonusProgressBar
      key={animatingSlotIndex}
      cardCollection={cardCollection}
      baseScore={
        (cardCollection.userCardCollection?.score || 0) -
        cardsScoreCount(newCards.slice(animatingSlotIndex || 0))
      }
      cardScore={
        typeof animatingSlotIndex === 'number'
          ? newCards.at(animatingSlotIndex)?.cardCollectionCards.at(0)
              ?.scoreBreakdown?.total || 0
          : 0
      }
      onThresholdReached={onThresholdReached}
      onAnimationEnd={onAnimationEnd}
      shouldAnimate={typeof animatingSlotIndex === 'number'}
    />
  );
};

AnimatedBonusProgressBar.fragments = {
  cardCollection: gql`
    fragment AnimatedBonusProgressBar_cardCollection on CardCollection {
      slug
      bonusThresholds {
        threshold
      }
      userCardCollection(forUserSlug: $userSlug) {
        id
        score
      }
      ...CollectionBonusProgressBar_cardCollection
    }
    ${CollectionBonusProgressBar.fragments.cardCollection}
  ` as TypedDocumentNode<AnimatedBonusProgressBar_cardCollection>,
  anyCardInterface: gql`
    fragment AnimatedBonusProgressBar_anyCardInterface on AnyCardInterface {
      slug
      cardCollectionCards {
        id
        scoreBreakdown {
          total
        }
      }
      ...cardsScoreCount_anyCardInterface
    }
    ${cardsScoreCount.fragments.anyCardInterface}
  ` as TypedDocumentNode<AnimatedBonusProgressBar_anyCardInterface>,
};
